import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';
import { ModalFormContainerComponent } from '../../../../../../components/ui/modal-form-container/modal-form-container.component';
import { cn } from '../../../../../../../utils/utils';
import { formatCount } from '../../../../../../../utils/sms';
import { SavePhoneNumberToTagComponent } from './partials/save-phone-number-to-tag/save-phone-number-to-tag.component';
import { ITag } from '../../../../../../types/contactModule';

import { ReactiveFormsModule, Validators, FormBuilder } from '@angular/forms';
import { LocalCsvService } from '../../../../../../services/local-csv/local-csv.service';
import { BroadcastMessageService } from '../../../../../../services/broadcast/broadcast.service';
import { Router } from '@angular/router';

@Component({
  selector: 'save-phone-numbers-component',
  standalone: true,
  imports: [
    ModalFormContainerComponent,
    SavePhoneNumberToTagComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './save-phone-numbers-component.component.html',
  styleUrl: './save-phone-numbers-component.component.css',
})
export class SavePhoneNumbersComponentComponent implements OnDestroy {
  localCSVService = inject(LocalCsvService);
  broadcastMessageService = inject(BroadcastMessageService);
  router = inject(Router);

  // received props
  @Input() showSavePhoneNumbersModal: boolean = false;
  @Input({ required: true }) messageDetails!: {
    phoneNumbers: string[]; //this represents the manually added ones
    contacts?: string[];
    tags?: string[];
    organizations?: string[];
    segments?: string[];
    senderID: string;
    messageSubject: string;
    template?: string;
    messageBody: string;
    messageType: 'Instant' | 'Scheduled';
    timeZone?: string | null;
    date?: string | null;
    time?: string | null;
    /** The schedule resolved in its time zone, passed through to the send. */
    scheduleInstant?: string | null;
  };

  // emitted events
  @Output() closeSavePhoneNumbersModalEvent = new EventEmitter();

  closeModal() {
    this.sendError = '';
    this.closeSavePhoneNumbersModalEvent.emit();
  }

  merge = cn;
  formatCount = formatCount;
  builder = inject(FormBuilder);

  get isScheduled(): boolean {
    return this.messageDetails?.messageType === 'Scheduled';
  }

  /**
   * The send failed or a step is incomplete: say so inside the dialog. Only
   * this dialog's own sends count, never the service's shared error text.
   */
  get problem(): string {
    return this.errorMsg || this.sendError;
  }

  /** Why the last send from this dialog failed; cleared when the next one starts. */
  sendError = '';

  /** Same wording as compose's direct send. */
  private describeSendError(err: any): string {
    const reason = err?.error?.message || err?.error?.detail || err?.error?.title;
    const action = this.isScheduled ? 'scheduled' : 'sent';
    if (typeof reason === 'string' && reason.trim()) {
      return `The message was not ${action}: ${reason.trim()}`;
    }
    if (err?.status === 0) {
      return `The message was not ${action}. Check your connection and try again.`;
    }
    return `The message was not ${action}. Try again, and contact support if it keeps failing.`;
  }

  private onSendError = (err: any) => {
    this.sendError = this.describeSendError(err);
  };

  saveNumbers: boolean = false;
  selectedTags: ITag[] = [];

  // feedback messages
  successMsg: string = '';
  errorMsg: string = '';

  // timeout IDs for cleanup
  private successTimeoutId?: ReturnType<typeof setTimeout>;
  private errorTimeoutId?: ReturnType<typeof setTimeout>;

  updateSuccessMsg(msg: string) {
    // Clear existing timeout before setting a new one
    if (this.successTimeoutId) {
      clearTimeout(this.successTimeoutId);
    }
    this.successMsg = msg;
    this.successTimeoutId = setTimeout(() => {
      this.successMsg = '';
      this.successTimeoutId = undefined;
    }, 5000);
  }

  updateErrorMsg(msg: string) {
    // Clear existing timeout before setting a new one
    if (this.errorTimeoutId) {
      clearTimeout(this.errorTimeoutId);
    }
    this.errorMsg = msg;
    this.errorTimeoutId = setTimeout(() => {
      this.errorMsg = '';
      this.errorTimeoutId = undefined;
    }, 5000);
  }

  // this is to save the phone numbers to either
  saveToExistingTags: boolean = true;
  updateSaveToExistingTags(val: boolean) {
    this.saveToExistingTags = val;
  }

  setSaveNumbers(val: boolean) {
    this.saveNumbers = val;
  }

  updateSelectedTags(tags: ITag[]) {
    this.selectedTags = tags;
  }

  // new tag field and related code
  submitted: boolean = false;
  newTagForm = this.builder.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    description: [''],
  });

  // this will be triggered provided the user does not want to save the phone numbers
  handleDontSavePhoneNumbers() {
    this.sendError = '';
    this.broadcastMessageService
      .sendMessage({
        ...this.messageDetails,
        saveContacts: false,
        phoneNumbers: this.getAllPhoneNumbers,
      })
      .subscribe({
        next: () => {
          this.router.navigate(['organization', 'messages']);
        },
        error: this.onSendError,
      });
  }

  // this is provided the user decides to save the phone numbers
  handleFinalSubmission() {
    this.sendError = '';
    // situation where user is saving to an existing tag
    if (this.saveToExistingTags) {
      if (!this.selectedTags.length) {
        this.updateErrorMsg('Choose at least one tag, or switch to creating a new tag.');
        return;
      } else {
        this.broadcastMessageService
          .sendMessage({
            ...this.messageDetails,
            saveContacts: true,
            phoneNumbers: this.getAllPhoneNumbers,
            saveToExistingTags: true,
            phoneNumberSelectedTags: this.selectedTags.map((el) => el.uuid!),
          })
          .subscribe({
            next: () => {
              this.router.navigate(['organization', 'messages']);
            },
            error: this.onSendError,
          });
      }
    } else {
      // situation where user is saving to a new tag created on the go
      this.submitted = true;
      if (this.newTagForm.valid) {
        this.broadcastMessageService
          .sendMessage({
            ...this.messageDetails,
            saveContacts: true,
            phoneNumbers: this.getAllPhoneNumbers,
            saveToExistingTags: false,
            newTag: {
              name: this.newTagForm.value.name!,
              description: this.newTagForm.value.description!,
            },
          })
          .subscribe({
            next: () => {
              this.router.navigate(['organization', 'messages']);
            },
            error: this.onSendError,
          });
      }
    }

    // provided user does not want to save the contacts
  }

  get getCSVFormattedFileData(): {
    filesCount: number;
    combinedCSVPhoneNumbers: string[];
  } {
    const filesCount = this.localCSVService.getAllFormattedFileData.length;

    let phoneNumbers: string[] = [];
    this.localCSVService.getAllFormattedFileData.forEach((el) => {
      const res = el.submitted.map((el) => el.phoneNumber);
      phoneNumbers = [...phoneNumbers, ...res];
    });

    return {
      filesCount,
      combinedCSVPhoneNumbers: phoneNumbers,
    };
  }

  get getAllPhoneNumbers(): string[] {
    return [
      ...this.messageDetails.phoneNumbers,
      ...this.getCSVFormattedFileData.combinedCSVPhoneNumbers,
    ];
  }

  get getTotalPhoneNumbers(): number {
    return this.getAllPhoneNumbers.length;
  }

  ngOnDestroy(): void {
    // Clear all timeouts when component is destroyed
    if (this.successTimeoutId) {
      clearTimeout(this.successTimeoutId);
    }
    if (this.errorTimeoutId) {
      clearTimeout(this.errorTimeoutId);
    }
  }
}
